import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

export type ReportStatus = 'PENDING' | 'UPLOADED' | 'COMPLETED';
export type ReportType = 'dexa_scan';

export interface MemberReportData {
  id: number;
  name: string;
  status: ReportStatus;
  report_date: string;
  report_type: ReportType;
  extracted_data: Record<string, unknown> | null;
  file_name: string;
  file_path: string;
  upload_date: string;
}

export interface MemberReportListResponse {
  member_id: string;
  results: MemberReportData[];
  total_count: number;
  status: string;
}

export interface MemberReportUploadRequest {
  file: File;
  name: string;
  status?: ReportStatus;
  report_date: string;
  report_type?: ReportType;
  extracted_data?: Record<string, unknown>;
}

export interface MemberReportDownloadResponse {
  id: number;
  name: string;
  status: ReportStatus;
  report_date: string;
  report_type: ReportType;
  extracted_data: Record<string, unknown> | null;
  file_name: string;
  file_path: string;
  upload_date: string;
  presigned_url: string;
  content_type: string;
  response_status: string;
}

export const getMemberReports = async (
  member_id: string,
  limit: number = 10,
  offset: number = 0
): Promise<MemberReportListResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}report/member/${member_id}`,
      { params: { limit, offset } }
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to fetch reports');
  }
};

export const uploadMemberReport = async (
  member_id: string,
  uploadData: MemberReportUploadRequest
): Promise<MemberReportData> => {
  try {
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('name', uploadData.name);
    formData.append('status', uploadData.status || 'UPLOADED');
    formData.append('report_date', uploadData.report_date);
    formData.append('report_type', uploadData.report_type || 'dexa_scan');
    if (uploadData.extracted_data) {
      formData.append('extracted_data', JSON.stringify(uploadData.extracted_data));
    }

    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}report/admin/upload/${member_id}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to upload report');
  }
};

export const downloadMemberReport = async (
  member_id: string,
  report_id: number
): Promise<string> => {
  try {
    const { data } = await axiosInstance.get<MemberReportDownloadResponse>(
      `${API_V1_PREFIX}report/download/${report_id}?member_id=${member_id}`
    );
    if (!data.presigned_url) {
      throw new Error('No presigned URL found in response');
    }
    return data.presigned_url;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to get download URL');
  }
};

export const deleteMemberReport = async (
  member_id: string,
  report_id: number
): Promise<{ id: number; status: string; message: string }> => {
  try {
    const { data } = await axiosInstance.delete(
      `${API_V1_PREFIX}report/${report_id}?member_id=${member_id}`
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to delete report');
  }
};

export const updateReportExtractedData = async (
  member_id: string,
  report_id: number,
  extracted_data: Record<string, unknown>
): Promise<MemberReportData> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}report/${report_id}/extracted-data`,
      { extracted_data, member_id }
    );
    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to update extracted data');
  }
};
